import {ChartType, CLChartData} from 'common/src/components/CLChart/CLChart.types';
import {DateTime} from 'luxon';
import {CLSelectorOption} from 'common/src/components/Selectors/CLSelector/CLSelector.types';

export interface RemoteCampaignMetric {
  caid: string;
  imp: number;
  clk: number;
  ctr: number;
  eng: number;
  ctc: number;
  vtc: number;
  cr: number;
  sales: number;
  viewability: number;
  qt1: number;
  mid: number;
  qt3: number;
  comp: number;
  skip: number;
  vcr: number;
  conv: number;
  ctcr: number;
  vtcr: number;
  view_y: number;
}

export interface CampaignMetric
  extends Omit<
    RemoteCampaignMetric,
    'caid' | 'imp' | 'clk' | 'eng' | 'qt1' | 'mid' | 'qt3' | 'comp' | 'view_y'
  > {
  campaignId: string;
  impressions: number;
  clicks: number;
  engagements: number;
  firstQuarter: number;
  secondQuarter: number;
  thirdQuarter: number;
  videoComplete: number;
  viewedImpressions: number;
}

export interface InsightsMetric {
  tableData: InsightsMetricTableData[];
}

export interface InsightsMetricTableData extends MetricsValue, DimensionsValue {
  id: number;
  isActive?: boolean;
  color?: string;
  name?: string;
  dateTimeDisplay?: string;
  adImage: string;
  creativeImage: string;
}

export interface InsightsMetricOptions {
  metricsOptions: InsightsMetricSelectOption<MetricValue>[];
  dimensionsOptions: InsightsMetricSelectOption<DimensionValue>[];
  savedViewOptions: InsightsMetricSavedViewsOption[];
}

export interface InsightsMetricSelectOption<Value extends string = string> {
  value: Value;
  label: string;
  icon?: string;
  isDisabled?: boolean;
  children?: InsightsMetricSelectOption[];
  unsupportedDimensions?: DimensionValue[];
  supportedMetrics?: MetricValue[];
  category?: string;
  eventType?: AnalyticsEventType;
}

export interface AnalyticsFilterItem {
  activeValue?: string;
  allValues?: string[];
  filterBy: string;
}

export interface RemoteInsightsMetricSavedViewsOption {
  filters: Record<DimensionValue, string>;
  fromInclusive: string;
  graphStyle: ChartType;
  id: string;
  metrics: MetricValue[];
  customConversions: MetricValue[];
  toExclusive: string;
  viewBy: DimensionValue[];
  viewName: string;
  metricsGroupBy: string;
  eventType: AnalyticsEventType;
}

export interface InsightsMetricSavedViewsOption {
  value: string;
  params: InsightsMetricSavedViewsOptionParams;
  label: string;
}

export interface DateRange {
  startDate: DateTime;
  endDate: DateTime;
}

export interface InsightsMetricSavedViewsOptionParams {
  metrics: MetricValue[];
  dimensions: DimensionValue[];
  viewName?: string;
  dateRange: DateRange;
  graphStyle: MetricsChartType;
  filters?: AnalyticsFilterItem[];
  groupBy: MetricsGroupBy;
  eventType: AnalyticsEventType;
}

export enum MetricValue {
  Clicks = 'clk',
  Sales = 'sales',
  Engagements = 'eng',
  Impressions = 'imp',
  Conversions = 'conv',
  ConversionRate = 'cr',
  ClickThroughRate = 'ctr',
  ViewThroughConversions = 'vtc',
  ClickThroughConversion = 'ctc',
  EngagementThroughConversions = 'etc',
  VideoQ1 = 'qt1',
  VideoQ2 = 'mid',
  VideoQ3 = 'qt3',
  VideoComplete = 'comp',
  Viewability = 'viewability',
  Skip = 'skip',
  VideoCompletionRate = 'vcr',
  CPC = 'cpc',
  CPA = 'cpa',
  Cost = 'cost',
  CPM = 'cpm',
  Roas = 'roas',
  ExternalCost = 'external_cost',
  ExternalConversions = 'external_conv',
  ExternalCPM = 'external_cpm',
  ExternalCPC = 'external_cpc',
  ExternalTotalConversionRate = 'external_cr',
  CustomConversions = 'custom_conv',
  CustomMetaConversions = 'custom_meta_conv',
  Reach = 'reach',
  UniquePersons = 'd_uid',
  UniqueHouseholds = 'd_hid',
  CTCR = 'ctcr',
  VTCR = 'vtcr',
  ViewedImpressions = 'view_y',
  ExternalSales = 'external_sales',
  ExternalRoas = 'external_roas',

  VideoQ1Rate = 'qt1r',
  VideoQ2Rate = 'midr',
  VideoQ3Rate = 'qt3r',
  Total = 'total',
}

export enum DimensionValue {
  DSP = 'dsp',
  Time = 'time',
  EventTime = 'event_time',
  TimeOfDay = 'time_of_day',
  DayOfWeek = 'day_of_week',
  Chart = 'chart',
  StoreID = 'bizid',
  Store = 'location',
  StoreName = 'location_name',
  UserDMA = 'dma',
  UserCity = 'ct',
  UserState = 'rename',
  UserStateShort = 're',
  Product = 'item',
  Device = 'device',
  AdFormat = 'frmt',
  UserCountry = 'cc',
  UserZipCode = 'zp',
  CampaignID = 'caid',
  CreativeID = 'csid',
  Date = 'event_date',
  Week = 'event_week',
  Month = 'event_month',
  Campaign = 'campaign',
  Creative = 'creative',
  PlacementID = 'plcid',
  Placement = 'placement',
  CampaignType = 'catype',
  StoreGroup = 'bizgrpid',
  ProductID = 'item_id',
  ProductName = 'item_title',
  DSPSegmentId = 'dsp_segid',
  ProductTitle = 'item_title',
  DSPStrategyId = 'dsp_strgid',
  CampaignName = 'campaign_name',
  CampaignStatus = 'campaign_status',
  CreativeName = 'creative_name',
  PlacementName = 'placement_name',
  Ad = 'external_ad',
  AdId = 'ad',
  AdName = 'external_ad_name',
  AdSet = 'external_ad_group',
  AdSetId = 'ad_group',
  AdSetName = 'external_ad_group_name',
  ExternalCampaignName = 'external_campaign_name',
  CreativeTemplateId = 'creative_template_id',

  CampaignTagOne = 'campaign_dim0',
  CampaignTagTwo = 'campaign_dim1',
  CampaignTagThree = 'campaign_dim2',
  CreativeTagOne = 'creative_tag1',
  CreativeTagTwo = 'creative_tag2',
  CreativeTagThree = 'creative_tag3',
  PlacementCustomOne = 'placement_custom1',
  PlacementCustomTwo = 'placement_custom2',
  PlacementCustomThree = 'placement_custom3',

  Attribution = 'attr',
  AttributionSignalID = 'attrsgnl',
  AttributionSignalName = 'attrsgnl_name',

  DSPCampaignID = 'dsp_caid',
  Client = 'client',
  ClientID = 'cid',
  ClientName = 'client_name',

  ProductCustomOne = 'item_custom0',
  ProductCustomTwo = 'item_custom1',
  ProductCustomThree = 'item_custom2',
  ProductTagOne = 'item_tag1',
  ProductTagTwo = 'item_tag2',
  ProductTagThree = 'item_tag3',

  ConversionType = 'convtype',
  DSPAdGroupId = 'dsp_grid',
  DSPCreativeId = 'dsp_crid',

  CartOutputId = 'output_id',
  CartOutputName = 'output_name',
  CampaignVersionName = 'caver_name',
  CampaignVersionNumber = 'caver_number',
  ConversionDate = 'convdate',
  DataSourceId = 'dsid',
  NodeId = 'nodeid',
  EventType = 'eventType',
}

export interface DashboardWidgetData {
  chartData: CLChartData;
  dimensionValues: Dimension;
}

export interface RemoteOptionalValue<Value extends MetricValue | DimensionValue>
  extends OptionalAnalyticsData {
  uiName: string;
  value: Value;
  children?: RemoteFilterOptionalValues[];
}

interface OptionalAnalyticsData {
  uiName: string;
  supportedMetrics: MetricValue[];
  category: string;
  unsupportedDimensions: DimensionValue[];
  label: string | null;
}

export interface RemoteOptionalMetrics extends OptionalAnalyticsData {
  value: MetricValue;
}

export interface RemoteOptionalDimensions extends OptionalAnalyticsData {
  value: DimensionValue;
}

export interface RemoteFilterOptionalValues {
  uiName: string;
  value: DimensionValue;
  children?: RemoteFilterOptionalValues[];
}

export type MetricsValue = Partial<Record<MetricValue, number>>;
export type DimensionsValue = Partial<Record<DimensionValue, string>>;
export type OptionsForDimensionValues = Record<
  string,
  {
    uiName: string;
    value: string;
  }
>;

export interface RemoteAnalyticsData extends MetricsValue, DimensionsValue {
  [key: string]: string | number | undefined;
}

export type AnalyticsData = RemoteAnalyticsData;
export type Dimension = Record<string, RemoteOptionalDimensions>;

export interface MetricsData {
  metric: MetricValue;
  dateRange: DateRange;
  dimension: DimensionValue;
  tableData: InsightsMetricTableData[];
  chartData?: CLChartData;
  apiFilters?: AnalyticsFilterItem[];
  metricsSelectedData?: MetricsSelectedData;
}

export interface MetricsSelectedData {
  ids: number[];
  colors: string[];
  chartFilter: AnalyticsFilterItem[];
}

export enum MetricsChartType {
  Bar = 'bar',
  Line = 'line',
  Area = 'area',
  HorizontalBar = 'horizontal_bar',
  PieChart = 'pie',
}

export enum MetricsGroupBy {
  Total = 'total',
  Day = 'day',
  Week = 'week',
  Month = 'month',
  ConvDate = 'convdate',
}

export enum MetricsAction {
  SaveView = 'Save view',
  ExportToPDF = 'Export to PDF',
  ExportToCSV = 'Export to CSV',
  CreateDashboardWidget = 'Create as Dashboard Widget',
}

export interface MetricAndDimensionOptions {
  metrics: InsightsMetricSelectOption<MetricValue>[];
  dimensions: InsightsMetricSelectOption<DimensionValue>[];
}

export enum AnalyticsSearchParamKey {
  Date = 'date',
  Timezone = 'timezone',
  Aggregation = 'aggregation',
  Filters = 'filters',
  ChartType = 'chartType',
  Dimensions = 'dimensions',
  Metrics = 'metrics',
  Widget = 'widget',
  EventType = 'eventType',
}

export interface RemoteCustomMetricsOption {
  key: string;
  uiName: string;
}

export interface AnalyticsDimensionForUiNames {
  name: string;
  description: string;
  tooltip: Record<string, string>;
}

export interface RemoteAnalyticsUiNames {
  metrics: Record<MetricValue, string>;
  customConversions: Record<string, string>;
  hasMetaCustomConversions: boolean;
  dimensions: Record<DimensionValue, AnalyticsDimensionForUiNames>;
}

export interface AnalyticsUiNames {
  customConversions: Record<string, string>;
  dimensions: Record<DimensionValue, string>;
  metrics: Record<MetricValue, string>;
  hasMetaCustomConversions: boolean;
  dimensionUiMetadataMap: Record<DimensionValue, AnalyticsDimensionForUiNames>;
}

export type AnalyticsUiNameMapping = Record<DimensionValue | MetricValue | string, string>;

export type AnalyticsTotalsWithTrends = Record<MetricValue, AnalyticsTotalsWithTrendsData>;
export type RemoteAnalyticsTotalsWithTrends = Record<
  MetricValue,
  RemoteAnalyticsTotalsWithTrendsData
>;

export interface RemoteAnalyticsTotalsWithTrendsData {
  fromInclusiveFirst: string;
  fromInclusiveSecond: string;
  percent: number;
  toExclusiveFirst: string;
  toExclusiveSecond: string;
  total: number;
}

export interface AnalyticsTotalsWithTrendsData {
  fromStartDate: DateTime;
  toStartDate: DateTime;
  fromEndDate: DateTime;
  toEndDate: DateTime;
  percent: number;
  total: number;
}

export interface CachedAnalytics {
  uiNameMapping: Record<DimensionValue | MetricValue | string, string>;
  optionsByMetricsKey: Partial<Record<MetricValue, CLSelectorOption[]>>;
  analyticsUiNamesLoaded: boolean;
  metaConversionsLoaded: boolean;
  isLoading: boolean;
  hasMetaCustomConversions: boolean;
  dimensionUiMetadataMap: Record<DimensionValue, AnalyticsDimensionForUiNames>;
}

export interface AnalyticsDataWithTrends {
  dimensions: DimensionsValue;
  metrics: Record<MetricValue, {percent: number; total: number}>;
}

export enum TableMetaData {
  CreativeImage = 'creativeImage',
  AdImage = 'adImage',
}

export enum AnalyticsEventType {
  Adserving = 'Adserving',
  Website = 'Website',
}

export enum AiAnalyticsEventType {
  Adserving = 'Ad Serving Events',
  Website = 'Website Events',
}

export type TableData = {
  id: number;
} & Partial<Record<MetricValue, number>> &
  Partial<Record<DimensionValue | TableMetaData, string>>;

export type TableDataWithTrends = TableData &
  Partial<
    Record<
      MetricValue,
      {
        total: number;
        percent: number;
      }
    >
  >;
